import React, { Fragment, lazy, Suspense, useContext, useState, useEffect, useRef } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, GridLegacy as Grid, useMediaQuery } from "@mui/material";
import { useParams } from "react-router-dom";

import NotchedCard from "../../../components/cards/NotchedCard/NotchedCard";
import { ProgressView } from "../../../components/notifications/progress/ProgressView";
import AssetHistoryTable from "./components/AssetHistoryTable";
import TabbedView from "../../../components/lists/TabbedView/TabbedView";
import TabbedViewPage from "../../../components/lists/TabbedView/TabbedViewPage";
import BookingHistoryTable from "./components/BookingHistoryTable";
import AnimateFrom from "../../../components/animations/AnimateFrom";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import SaleItemBadges from "./components/SaleItemBadges";
import PaymentHistoryTable from "./components/PaymentHistoryTable";
import ProductHistoryTable from "./components/ProductHistoryTable";
import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";
import AlertDialog from "../../../components/notifications/alertDialog/AlertDialog";
import ProductsCompletedList from "./components/ProductsCompletedList";

import {
  api,
  AssetVehicle,
  Booking,
  BookingStatus,
  CollectionDeliveryStatus,
  IBookingMoveVehicleAndKey,
  LaneAllocationSystem,
  MovementReasonType,
  RemarkType,
  RequestStatus,
  SourceApp,
  SystemProfile,
  VehicleStatus,
} from "../../../logic/api";

import flex from "../../../styles/flex";
import palette from "../../../styles/palette";
import styles from "../styles";

import Scaler from "../../dash/components/scaler/Scaler";
import DashPage from "../../dash/DashPage";
import NotFoundPage from "../../error/NotFoundPage";
import BookingFields from "../BookingFields/BookingFields";
import BookingFab, { BookingFabController } from "../BookingFab/BookingFab";
import WorkflowBooking from "../../workflow/components/booking/WorkflowBooking";
import BookingProducts from "../BookingProducts/BookingProducts";
import IngestionView from "../../admin/IngestionView";
import { BookingComplaints } from "../BookingComplaints/BookingComplaints";
import { BookingInspections } from "../BookingInspections/BookingInspections";

import { isSysAdmin, uuidRegex } from "../../../utility/utils";
import { SessionContext } from "../../../utility/useSession";
import { useDate } from "../../../utility/useDate";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";
import PimTransactionsView from "../../mobile/devices_admin/PimTransactionsView";
import StatusBadges from "./components/StatusBadges";
import CheckoutConfirmDialog from "./components/CheckoutConfirmDialog";
import AuditHistoryTable from "./components/AuditHistoryTable";
import CheckoutConfirmDialogV2 from "./components/CheckoutConfirmDialogV2";
import UndoCheckOutDialog from "./components/UndoCheckoutDialog";
import { useTranslation } from "react-i18next";
import { CheckInDialog } from "./components/CheckInConfirmDialog";
import BookingDataRetention from "../BookingDataRetention/BookingDataRetention";
import ConfirmationDialog from "../../../components/notifications/confirmationDialog/ConfirmationDialog";
import VehicleAllocationPickerDialog from "../BookingMovement/VehicleAllocationPickerDialog";
import UndoCheckInDialog from "./components/UndoCheckinDialog";
import { usePreferences } from "../../../utility/usePreferences";
import BookingFabV2 from "../BookingFab/BookingFabV2";
import { FindKeyDialog } from "../BookFindKey/BookingFindKey";
import AccessDeniedMessage from "@/components/errors/AccessDeniedMessage";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { SnackAlert } from "@/components/notifications/snack/SnackAlert";

const BookingRemarks = lazy(() => import("../BookingView/components/BookingRemarks"));
const BookingRemarksV2 = lazy(() => import("../BookingView/components/BookingRemarksV2"));

const getCollectionDeliveryTranslationKey = (
  status: CollectionDeliveryStatus,
  variant: "progress" | "success" | "fail"
): string | null => {
  const keys: Partial<Record<CollectionDeliveryStatus, Record<string, string>>> = {
    [CollectionDeliveryStatus.CollectionRequested]: {
      progress: "booking.labels.confirmingArrival",
      success: "booking.labels.collectionRequestedSuccess",
      fail: "booking.labels.collectionRequestedFailed",
    },
    [CollectionDeliveryStatus.Pending]: {
      progress: "booking.labels.undoingCollectionRequested",
      success: "booking.labels.collectionRequestedUndone",
      fail: "booking.labels.arrivalUndoFailed",
    },
    [CollectionDeliveryStatus.DeliveryRequested]: {
      progress: "booking.labels.confirmingReturn",
      success: "booking.labels.deliveryRequestedSuccess",
      fail: "booking.labels.deliveryRequestedFailed",
    },
    [CollectionDeliveryStatus.CollectionComplete]: {
      progress: "booking.labels.undoingDeliveryRequested",
      success: "booking.labels.deliveryRequestedUndone",
      fail: "booking.labels.returnUndoFailed",
    },
    [CollectionDeliveryStatus.DeliveryComplete]: { progress: "", success: "", fail: "" },
  };
  return keys[status]?.[variant] || null;
};

const getCollectionDeliveryDisplay = (
  status?: CollectionDeliveryStatus | null
): { key: string; color: string } | null => {
  switch (status) {
    case CollectionDeliveryStatus.CollectionRequested:
      return {
        key: "booking.collectionDeliveryStatus.collectionRequested",
        color: "red",
      };
    case CollectionDeliveryStatus.CollectionInProgress:
      return {
        key: "booking.collectionDeliveryStatus.collectionInProgress",
        color: "rgb(3, 102, 214)",
      };
    case CollectionDeliveryStatus.CollectionComplete:
      return {
        key: "booking.collectionDeliveryStatus.collectionComplete",
        color: "green",
      };
    case CollectionDeliveryStatus.DeliveryRequested:
      return {
        key: "booking.collectionDeliveryStatus.deliveryRequested",
        color: "red",
      };
    case CollectionDeliveryStatus.DeliveryInProgress:
      return {
        key: "booking.collectionDeliveryStatus.deliveryInProgress",
        color: "rgb(3, 102, 214)",
      };
    case CollectionDeliveryStatus.DeliveryComplete:
      return {
        key: "booking.collectionDeliveryStatus.deliveryComplete",
        color: "green",
      };
    default:
      return null;
  }
};

const BookingViewPage = () => {
  const { localizeDate, toFormattedDateTimeString } = useDate();
  const showOnSmUp = useMediaQuery((theme) => theme.breakpoints.up('sm'));
  const showOnXsDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { hasPermission } = useUserPermissions();

  const { bookingId: rawParam } = useParams<{ bookingId: string }>();

  // 2) decode/parse into either a numeric `id` or a `uuid`
  let id: number | undefined;
  let uuid: string | undefined;

  try {
    // if it's base-64 encoded "booking:<val>"
    const decoded = atob(rawParam);
    const val = decoded.split("booking:")[1];

    if (/^\d+$/.test(val)) {
      id = parseInt(val, 10);
    } else if (uuidRegex.test(val)) {
      uuid = val;
    }
  } catch {
    // not valid base-64 → maybe it's already a raw UUID
    if (uuidRegex.test(rawParam)) {
      uuid = rawParam;
    }
  }

  const session = useContext(SessionContext);
  const { t } = useTranslation();

  const [booking, setBooking] = useState<Booking | null>(null);
  const updateBookingApi = api.hooks.booking.update();
  const moveVehicleAndKey = api.hooks.booking.moveVehicleAndKey();
  const fetchBooking = api.hooks.booking.read();
  const fetchByUUID = api.hooks.booking.getByUUID();
  const getSalesAndPayments = api.hooks.booking.getSalesAndPayments();
  const requestCancelBooking = api.hooks.booking.cancelBooking();
  const requestUnArchiveBooking = api.hooks.booking.unArchiveBooking();
  const requestRestoreBooking = api.hooks.booking.uncancelBooking();
  const requestAllowKey = api.hooks.keyLockerStd.allowCustomerKey();
  const listRemarks = api.hooks.booking.listRemarks();
  const requestAllocateBooking = api.hooks.allocation.allocateBooking();
  const updateCollectionDeliveryStatus = api.hooks.booking.updateCollectionDeliveryStatus();
  const { allocationSystem, useTrackingId } = usePreferences();

  const [showCheckIn, setShowCheckIn] = useState(false);
  const [showCheckOut, setShowCheckOut] = useState(false);
  const [showUndoCheckIn, setShowUndoCheckIn] = useState(false);
  const [showUndoCheckOut, setShowUndoCheckOut] = useState(false);
  const [showCancelBooking, setShowCancelBooking] = useState(false);
  const [showUnArchiveBooking, setShowUnArchiveBooking] = useState(false);
  const [showRestoreBooking, setShowRestoreBooking] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);

  const [showCheckInComplete, setShowCheckInComplete] = useState(false);
  const [showCheckOutComplete, setShowCheckOutComplete] = useState(false);
  const [showUndoCheckInComplete, setShowUndoCheckInComplete] = useState(false);
  const [showUndoCheckOutComplete, setShowUndoCheckOutComplete] = useState(false);
  const [showComplaints, setShowComplaints] = useState(false);
  const [showInspections, setShowInspections] = useState(false);
  const [showDataRetention, setShowDataRetention] = useState(false);
  const [showAllowKeyRelease, setShowAllowKeyRelease] = useState(false);
  const [showAllowKeyReleaseComplete, setShowAllowKeyReleaseComplete] = useState(false);
  const [showRePrintKeyTag, setShowRePrintKeyTag] = useState(false);
  const [showTimeSlotChanged, setShowTimeSlotChanged] = useState(false);
  const [showAllocateVehicle, setShowAllocateVehicle] = useState(false);
  const [showFindKey, setShowFindKey] = useState(false);
  const [remarkType, setRemarkType] = useState<RemarkType>(RemarkType.Internal);
  const remarksSectionRef = useRef<HTMLDivElement>(null);
  const collectionDeliveryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restoreTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [bookingNumericId, setBookingNumericId] = useState<number | null>(null);
  const [allocationSnackbarInfo, setAllocationSnackbarInfo] = useState<string>("");
  const [isAllocating, setIsAllocating] = useState<boolean>(false);
  const [collectionDeliverySnackbarInfo, setCollectionDeliverySnackbarInfo] = useState<string>("");
  const [isUpdatingCollectionDelivery, setIsUpdatingCollectionDelivery] = useState<boolean>(false);
  const [restoreSnackbarInfo, setRestoreSnackbarInfo] = useState("");
  const [isRestoreProgressVisible, setIsRestoreProgressVisible] = useState(false);

  const deprecateArchiveReleased = useReleaseManagement(ReleaseFlag.releaseDeprecateArchiveStatus);
  const editableBookingCardFlag = useReleaseManagement(ReleaseFlag.releaseEditableBookingCard);
  const checkoutDialogV2Released = useReleaseManagement(ReleaseFlag.checkoutDialogV2);
  const unknownMovementReleased = useReleaseManagement(ReleaseFlag.releaseUnknownMovement);
  const displayTimeSlotFlag = useReleaseManagement(ReleaseFlag.releaseDisplayTimeSlot);
  const isDataManagementReleased = useReleaseManagement(ReleaseFlag.releaseDataManagement);
  const kmosManualAllocationReleased = useReleaseManagement(ReleaseFlag.kmosManualAllocationReleased);
  const bookingRemarksV2Released = useReleaseManagement(ReleaseFlag.releaseBookingRemarksV2);
  const dualParkAllocationReleased = useReleaseManagement(ReleaseFlag.releaseSydneyDualParkAllocation);
  const isChangeOrderOfFabMenu = useReleaseManagement(ReleaseFlag.releaseChangeOrderOfFabMenu);
  const commentsOnCanceledBookingsReleased = useReleaseManagement(ReleaseFlag.releaseCommentsOnCanceledBookings);
  const releaseAdditionalExternalReferences = useReleaseManagement(ReleaseFlag.releaseAdditionalExternalReferences);
  const correctionMovementReleased = useReleaseManagement(ReleaseFlag.releaseMarkVehicleMovementAsCorrection);
  const correctDualParkManualAllocation = useReleaseManagement(ReleaseFlag.releaseDualParkVehiclesShowingInLocationHierarchy);
  const headerPanelReleased = useReleaseManagement(ReleaseFlag.releaseBookingHeaderPanel);
  const isCollectionDeliveryHeaderStatusReleased = useReleaseManagement(ReleaseFlag.releaseCollectionDeliveryBookingHeaderStatus);
  const isRestoreBookingUncancelReleased = useReleaseManagement(ReleaseFlag.releaseBookingUncancel);

  const paymentsSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (restoreTimeoutRef.current) {
        clearTimeout(restoreTimeoutRef.current);
      }
    };
  }, []);

  const bookingFabController = new BookingFabController();

  function getVehicleStatusKey(statusId: VehicleStatus) {
    switch (statusId) {
      case VehicleStatus.NotCheckedIn:
        return 'booking.vehicleStatus.notCheckedIn';
      case VehicleStatus.PreCheckIn:
        return 'booking.vehicleStatus.preCheckIn';
      case VehicleStatus.CheckedIn:
        return 'booking.vehicleStatus.checkedIn';
      case VehicleStatus.CheckedOut:
        return 'booking.vehicleStatus.checkedOut';
      default:
        return 'booking.vehicleStatus.unknown';
    }
  }

  const statusKey = booking ? getVehicleStatusKey(booking.vehicle.statusId) : '';
  const translatedStatus = statusKey ? t(statusKey) : '';
  const collectionDeliveryDisplay = booking
    ? getCollectionDeliveryDisplay(booking.vehicle.collectionDeliveryStatus)
    : null;
  const shouldShowCollectionDeliveryStatus =
    !!booking &&
    booking.statusId !== BookingStatus.Cancelled &&
    isCollectionDeliveryHeaderStatusReleased &&
    (booking.collection || booking.delivery) &&
    collectionDeliveryDisplay != null;
  const renderCollectionDeliveryStatus = () =>
    shouldShowCollectionDeliveryStatus && collectionDeliveryDisplay ? (
      <>
        <Box component="span" sx={{ color: palette.text.secondary }}>
          {" • "}
        </Box>
        <Box component="span" sx={{ color: collectionDeliveryDisplay.color }}>
          {t(collectionDeliveryDisplay.key)}
        </Box>
      </>
    ) : null;

  let booking_remarks_always_enabled = false;
  if (session.current.site != null) {
    booking_remarks_always_enabled = session.current.site.preferences.booking_remarks_always_enabled;
  }

  const getNewKey = () => {
    return new Date().getTime().toString();
  };

  const [bookingHistoryTableKey, setBookingHistoryTableKey] = useState<
    string | null
  >(null);
  const [assetHistoryTableKey, setAssetHistoryTableKey] = useState<
    string | null
  >(null);
  const [productHistoryTableKey, setProductHistoryTableKey] = useState<
    string | null
  >(null);

  let readOnly = false;

  if (session.current.site) {
    const bk: Booking = booking as Booking;
    if (bk != null) {
      if (bk.carPark != null) {
        if (bk.carPark.id != session.current.site.home.id) {
          readOnly = true;
        }
      }
    }
  }

  if (booking) {
    if (booking.statusId == BookingStatus.Cancelled) {
      readOnly = true;
    }
  }



  let allowUpdatingBooking = true;
  let booking_products_enabled = true;
  let booking_payments_enabled = true;
  let booking_packages_enabled = true;
  let useFirstName = false;
  let system_profile: SystemProfile = SystemProfile.airportParking;

  if (session != null) {
    if (session.current != null) {
      if (session.current.site != null) {
        if (session.current.site.preferences.system_profile != null) {
          system_profile = session.current.site.preferences.system_profile;
        }

        if (session.current.site.preferences.booking_update_enabled == false) {
          allowUpdatingBooking = false;
        }

        booking_products_enabled = session.current.site.preferences.booking_products_enabled == true;
        booking_payments_enabled = session.current.site.preferences.booking_payments_enabled == true;
        booking_packages_enabled = session.current.site.preferences.booking_packages_enabled == true;
        useFirstName = session.current.site.preferences.booking_leadContact_firstName_enabled == true;
      }
    }
  }

  useEffect(() => {
    if (id != null) {
      fetchBooking.send(session.current.context, { id });
    } else if (uuid) {
      fetchByUUID.send(session.current.context, { uuid });
    } else {
      console.warn("Invalid booking parameter:", rawParam);
    }
  }, [id, uuid]);

  const setBookingDataAndRefresh = (data: Booking) => {
    setBooking(data);
    setBookingNumericId(data.id);
    listRemarks.send(session.current.context, { bookingId: data.id });
    getSalesAndPayments.send(session.current.context, { bookingId: data.id });
  }

  useEffect(() => {
    if (!fetchBooking.data) return;
    setBookingDataAndRefresh(fetchBooking.data);
  }, [fetchBooking.data]);

  useEffect(() => {
    if (!fetchByUUID.data) return;
    setBookingDataAndRefresh(fetchByUUID.data);
  }, [fetchByUUID.data]);

  async function refreshBooking() {
    if (bookingNumericId == null) return;
    fetchBooking.send(session.current.context, { id: bookingNumericId });
  }

  async function refreshSalesAndProducts() {
    if (bookingNumericId == null) return;
    getSalesAndPayments.send(session.current.context, { bookingId: bookingNumericId });
  }

  const saveBooking = async (data: Booking) => {
    return new Promise<boolean>((accept) => {
      updateBookingApi.send(session.current.context, data, (updatedBooking) => {
        if (updatedBooking == null) {
          accept(false);
          return;
        }

        let bookingDetailsChanged = booking == null || (updatedBooking.returnFlight.flightDate != booking.returnFlight.flightDate);
        if (editableBookingCardFlag) {
          bookingDetailsChanged = bookingDetailsChanged === true || (updatedBooking.bookingStartDate != booking?.bookingStartDate || updatedBooking.bookingEndDate != booking?.bookingEndDate);
        }

        setBooking(updatedBooking);
        setBookingHistoryTableKey(getNewKey());
        if (bookingDetailsChanged == true) {
          refreshSalesAndProducts();
        }

        let kmosPromptReallocateEnabled;
        if (dualParkAllocationReleased) {
          kmosPromptReallocateEnabled = session?.current?.site?.preferences.kmos_prompt_reallocate_enabled == true && session?.current?.site?.preferences.lane_allocation_system == LaneAllocationSystem.KMOS;
        } else {
          kmosPromptReallocateEnabled = session?.current?.site?.preferences.kmos_prompt_reallocate_enabled == true;
        }
        if (kmosManualAllocationReleased && kmosPromptReallocateEnabled && updatedBooking.timeSlotSegmentHasChanged == true) {
          setShowTimeSlotChanged(true);
        }
        accept(true);
        return;
      });
    });
  };

  const movement = async (data: Booking) => {
    const getManualLaneAllocationId = () => {
      if (dualParkAllocationReleased) {
        //Dual Park will always attempt to manually reallocate a vehicle upon movement
        if (allocationSystem === LaneAllocationSystem.DualPark &&
          (
            correctDualParkManualAllocation === false ||
            data.vehicle.allocatedLane?.updated === true
          )
        ) {
          return data.vehicle.allocatedLane?.id;
        }

        if (allocationSystem === LaneAllocationSystem.KMOS) {
          if (!kmosManualAllocationReleased || !data.vehicle.allocatedLane?.updated) {
            return undefined;
          } else {
            return data.vehicle.allocatedLane.id;
          }
        }
      } else {
        return (kmosManualAllocationReleased == false || data.vehicle.allocatedLane == null
          ? undefined
          : data.vehicle.allocatedLane.updated != true
            ? undefined
            : data.vehicle.allocatedLane.id)
      }
    }

    return new Promise<boolean>((accept) => {
      const input: IBookingMoveVehicleAndKey = {
        bookingId: data.id,
        bookingRef: data.ref,
        movementDate: localizeDate(new Date()).toISO() ?? "",
        sourceApp: SourceApp.frontEnd,
        vehicleLocationId:
          data.vehicle.location == null
            ? undefined
            : data.vehicle.location.updated != true
              ? undefined
              : data.vehicle.location.id,
        keyLocationId:
          data.vehicleKey.location == null
            ? undefined
            : data.vehicleKey.location.updated != true
              ? undefined
              : data.vehicleKey.location.id,
        manualLaneAllocationId: getManualLaneAllocationId(),
        vehicleMovementReason: correctionMovementReleased && data.vehicle.movedBy?.firstName === t("booking.labels.correction") ? MovementReasonType.correction : undefined,
        keyMovementReason: correctionMovementReleased && data.vehicleKey.movedBy?.firstName === t("booking.labels.correction") ? MovementReasonType.correction : undefined,
      };

      input.keyMovedById = correctionMovementReleased && data.vehicleKey.movedBy?.firstName === t("booking.labels.correction") ? undefined : data.vehicleKey.location?.updated ? data.vehicleKey.movedBy?.id : undefined;
      input.vehicleMovedById = correctionMovementReleased && data.vehicle.movedBy?.firstName === t("booking.labels.correction") ? undefined : data.vehicle.location?.updated ? data.vehicle.movedBy?.id : undefined;

      moveVehicleAndKey.send(
        session.current.context,
        input,
        (updatedBooking) => {
          if (updatedBooking == null) {
            if (unknownMovementReleased) {
              refreshBooking();
            }
            accept(false);
            return;
          }
          if (kmosManualAllocationReleased && session?.current?.site?.preferences.kmos_manual_allocation_enabled == true && updatedBooking.vehicle?.allocatedLane?.updated == true) {
            setShowRePrintKeyTag(true);
          }
          setBooking({ ...updatedBooking });
          refreshBookingDisplay(updatedBooking);
          setAssetHistoryTableKey(getNewKey());
          accept(true);
          return;
        }
      );
    });
  };

  const printLabels = async (booking: Booking) => {
    await bookingFabController.printCheckInBarcodes(booking);
    booking.labelsPrinted = true;
    await saveBooking(booking);
  }

  const allowReleaseKey = async () => {
    setShowAllowKeyRelease(false);
    if (booking) {
      requestAllowKey.send(
        session.current.context,
        {
          ref: booking.ref,
        },
        (updatedBooking) => {
          if (updatedBooking == null) {
            return;
          }
          setBookingHistoryTableKey(getNewKey());
          setShowAllowKeyReleaseComplete(true);
        }
      );
    }
  }

  const allocateBooking = async () => {
    if (booking) {
      setIsAllocating(true);
      setAllocationSnackbarInfo(t('booking.allocation.allocating', {
        bookingRef: booking.ref
      }));

      requestAllocateBooking.send(session.current.context, {
        bookingId: booking.id
      }, (result: AssetVehicle | null) => {
        setIsAllocating(false);

        if (!result) {
          setAllocationSnackbarInfo(t('booking.allocation.error', {
            bookingRef: booking.ref
          }));

          return;
        }

        if (result.allocatedRow) {
          setAllocationSnackbarInfo(t('booking.allocation.updatedWithRow', {
            bookingRef: booking.ref,
            lane: result.allocatedLane?.name,
            row: result.allocatedRow
          }));
        } else if (result.allocatedLane) {
          setAllocationSnackbarInfo(t('booking.allocation.updated', {
            bookingRef: booking.ref,
            lane: result.allocatedLane?.name
          }));
        } else {
          setAllocationSnackbarInfo(t('booking.allocation.failed', {
            bookingRef: booking.ref
          }));
        }

        booking.vehicle = result;
        refreshBooking();
      });
    }
  }

  const handleUpdateCollectionDeliveryStatus = (bookingUuid: string, status: CollectionDeliveryStatus) => {
    const progressKey = getCollectionDeliveryTranslationKey(status, "progress");

    if (collectionDeliveryTimeoutRef.current) {
      clearTimeout(collectionDeliveryTimeoutRef.current);
    }

    collectionDeliveryTimeoutRef.current = setTimeout(() => {
      if (progressKey) {
        setIsUpdatingCollectionDelivery(true);
        setCollectionDeliverySnackbarInfo(t(progressKey));
      }
    }, 300);

    updateCollectionDeliveryStatus.send(
      session.current.context,
      { bookingUuid, collectionDeliveryStatus: status },
      (updatedBooking) => {
        if (collectionDeliveryTimeoutRef.current) {
          clearTimeout(collectionDeliveryTimeoutRef.current);
          collectionDeliveryTimeoutRef.current = null;
        }
        setIsUpdatingCollectionDelivery(false);

        if (updatedBooking && booking) {
          setBooking({
            ...booking,
            vehicle: {
              ...booking.vehicle,
              collectionDeliveryStatus: updatedBooking.vehicle.collectionDeliveryStatus
            }
          });
          refreshBooking();

          const successKey = getCollectionDeliveryTranslationKey(status, "success");
          if (successKey) {
            setCollectionDeliverySnackbarInfo(t(successKey));
          }
        } else {
          const failKey = getCollectionDeliveryTranslationKey(status, "fail");
          if (failKey) {
            setCollectionDeliverySnackbarInfo(t(failKey));
          } else {
            setCollectionDeliverySnackbarInfo("");
          }
        }
      }
    );
  };

  const checkOutBooking = async () => {
    setShowCheckOut(true);
  };

  const undoCheckOut = async () => {
    setShowUndoCheckOut(true);
  }

  const cancelBooking = async () => {
    return new Promise((accept) => {
      if (showCancelBooking == false) {
        setShowCancelBooking(true);
        accept(true);
        return;
      }
      setShowCancelBooking(false);
      if (booking) {
        requestCancelBooking.send(
          session.current.context,
          {
            id: booking.id,
            dated: localizeDate(new Date()).toISO() ?? "",
            cancelSource: SourceApp.frontEnd,
          },
          (updatedBooking) => {
            if (updatedBooking == null) {
              accept(false);
              return;
            }
            setBooking(updatedBooking);
            setBookingHistoryTableKey(getNewKey());
            setAssetHistoryTableKey(getNewKey());
            accept(true);
          }
        );
      }
    });
  };

  const unArchiveBooking = async () => {
    return new Promise((accept) => {
      if (showUnArchiveBooking == false) {
        setShowUnArchiveBooking(true);
        accept(true);
        return;
      }
      setShowUnArchiveBooking(false);
      if (booking) {
        requestUnArchiveBooking.send(
          session.current.context,
          {
            id: booking.id,
            dated: localizeDate(new Date()).toISO() ?? "",
          },
          (updatedBooking) => {
            if (updatedBooking == null) {
              accept(false);
              return;
            }
            setBooking(updatedBooking);
            setBookingHistoryTableKey(getNewKey());
            setAssetHistoryTableKey(getNewKey());
            accept(true);
          }
        );
      }
    });
  };

  const restoreBooking = () => {
    if (!booking) return;

    setShowRestoreBooking(false);
    setRestoreSnackbarInfo("");
    restoreTimeoutRef.current = setTimeout(() => {
      setIsRestoreProgressVisible(true);
      setRestoreSnackbarInfo(t("booking.restore.progress"));
    }, 300);

    requestRestoreBooking.send(
      session.current.context,
      {
        uuid: booking.uuid,
        restoreSource: SourceApp.frontEnd,
      },
      (restoredBooking) => {
        if (restoreTimeoutRef.current) {
          clearTimeout(restoreTimeoutRef.current);
          restoreTimeoutRef.current = null;
        }
        setIsRestoreProgressVisible(false);

        if (!restoredBooking) {
          setRestoreSnackbarInfo(t("booking.restore.failure"));
          return;
        }

        setBooking(restoredBooking);
        setBookingHistoryTableKey(getNewKey());
        setAssetHistoryTableKey(getNewKey());
        setRestoreSnackbarInfo(t("booking.restore.success"));
      }
    );
  };


  const expectedReturnDate = !booking ? "" : toFormattedDateTimeString(new Date(booking.expectedReturnDate));

  const ellipsis = (text: string, maxLength: number) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "..";
    }
    return text;
  }

  const onCheckInComplete = (booking: Booking | null) => {
    if (!booking) {
      return;
    }

    setBooking(booking);
    printLabels(booking);
    setShowCheckInComplete(true);
    setBookingHistoryTableKey(getNewKey());
    refreshSalesAndProducts();
    setAssetHistoryTableKey(getNewKey());
    setShowCheckIn(false);
  }

  const onCheckInCancel = () => {
    setShowCheckIn(false);
  }

  // Part of CheckoutDialogV2
  const onCheckedOut = (booking: Booking | null) => {
    setShowCheckOut(false);

    if (!booking) {
      return;
    }

    refreshBookingDisplay(booking);
    setShowCheckOutComplete(true);
  }

  const onUndoCheckIn = (booking: Booking | null, error?: string) => {
    if (!error) {
      setShowUndoCheckIn(false);
      setShowUndoCheckInComplete(true);
    }

    if (!booking) {
      return;
    }

    refreshBookingDisplay(booking);
  }

  const onUndoCheckOut = (booking: Booking | null, error?: string) => {
    if (!error) {
      setShowUndoCheckOut(false);
      setShowUndoCheckOutComplete(true);
    }

    if (!booking) {
      return;
    }

    refreshBookingDisplay(booking);
  }

  const onDataRetentionConfirm = (booking: Booking | null, error?: string) => {
    if (!error) {
      setShowDataRetention(false);
    }

    if (!booking) {
      return;
    }

    refreshBooking();
  }

  const refreshBookingDisplay = (booking: Booking) => {
    setBooking(booking);
    setBookingHistoryTableKey(new Date().getTime().toString());
    setAssetHistoryTableKey(new Date().getTime().toString());
    refreshSalesAndProducts();
  }

  const timeslot = displayTimeSlotFlag && booking && booking.vehicle.timeSlot && <strong style={{ color: "grey" }}>{booking.vehicle.timeSlot}</strong>;

  if (!hasPermission("bookingManagement.booking.read")) {
    return (
      <DashPage>
        <AccessDeniedMessage />
      </DashPage>
    );
  }

  const isLoading = fetchBooking.status === RequestStatus.loading || fetchByUUID.status === RequestStatus.loading;
  const isRestoreRequestPending = requestRestoreBooking.status === RequestStatus.loading;
  if (isLoading) {
    return (
      <DashPage>
        <ProgressView status={RequestStatus.loading} />
      </DashPage>
    );
  }

  const isInvalidBooking = bookingNumericId == null || isNaN(bookingNumericId) || (booking != null && booking.id == null);
  if (isInvalidBooking) {
    return (
      <NotFoundPage
        wrapWithDash={true}
        title={t("errors.bookingNotFound.title")}
        message={t("errors.bookingNotFound.message")}
      />
    );
  }

  return (
    <DashPage>
      <div style={styles.wrapper}>
        <div style={styles.container}>

          <UndoCheckOutDialog
            booking={booking}
            salesAndPayments={getSalesAndPayments.data}
            display={showUndoCheckOut}
            cancel={() => setShowUndoCheckOut(false)}
            onUndoCheckOut={onUndoCheckOut}
          />

          <UndoCheckInDialog
            booking={booking}
            display={showUndoCheckIn}
            cancel={() => setShowUndoCheckIn(false)}
            onUndoCheckIn={onUndoCheckIn}
          />

          {booking && isDataManagementReleased && (
            <BookingDataRetention
              booking={booking}
              display={showDataRetention}
              cancel={() => setShowDataRetention(false)}
              onDataRetentionConfirm={onDataRetentionConfirm} />
          )}

          {(checkoutDialogV2Released) ? (
            <CheckoutConfirmDialogV2
              booking={booking}
              salesAndPayments={getSalesAndPayments.data}
              display={showCheckOut}
              cancel={() => setShowCheckOut(false)}
              refresh={() => refreshBooking()}
              showPaymentDialog={() => setShowPaymentDialog(true)}
              onCheckedOut={onCheckedOut}
            />
          ) : (
            <CheckoutConfirmDialog
              booking={booking}
              callbacks={{ refreshSalesAndProducts, showPaymentDialog: () => setShowPaymentDialog(true) }}
              display={showCheckOut}
              session={session}
              stateHooks={{ setAssetHistoryTableKey, setBooking, setBookingHistoryTableKey, setShowCheckOut, setShowCheckOutComplete }}
            />
          )}

          <ErrorDialog err={updateBookingApi.err} />
          <ErrorDialog err={moveVehicleAndKey.err} />
          <ErrorDialog err={requestCancelBooking.err} />
          <ErrorDialog err={requestUnArchiveBooking.err} />
          <ErrorDialog err={requestAllowKey.err} />

          <ProgressDialog
            delay={2000}
            status={
              booking != null ? fetchBooking.status : RequestStatus.loaded
            }
          />
          <ProgressDialog status={updateBookingApi.status} />

          <ProgressDialog status={moveVehicleAndKey.status} />
          <ProgressDialog
            status={requestCancelBooking.status}
          />
          <ProgressDialog
            status={requestUnArchiveBooking.status}
          />
          <ProgressDialog
            status={requestAllowKey.status}
          />

          {booking != null && (<BookingComplaints booking={booking} show={showComplaints} onHide={(didSave) => {
            setShowComplaints(false);
            if (didSave == true) {
              setBooking({ ...booking, hasComplaint: true });
            }
          }} />)}

          {booking != null && (<FindKeyDialog booking={booking} show={showFindKey} onHide={() => {
            setShowFindKey(false);
          }} />)}

          {booking != null && (<BookingInspections booking={booking} show={showInspections} onHide={() => {
            setShowInspections(false);
          }} />)}
          <ConfirmationDialog
            title={t("confirmationDialog.rePrintKeyTag.title")}
            message={t("confirmationDialog.rePrintKeyTag.message")}
            buttonCancellationText={t("confirmationDialog.rePrintKeyTag.cancel")}
            buttonConfirmationText={t("confirmationDialog.rePrintKeyTag.confirm")}
            show={showRePrintKeyTag}
            onClose={(shouldReprint) => {
              if (shouldReprint && booking != null) {
                printLabels(booking);
              }
              setShowRePrintKeyTag(false);
            }}

          />
          <ConfirmationDialog
            title={t("confirmationDialog.timeslotChanged.title")}
            message={t("confirmationDialog.timeslotChanged.message")}
            buttonCancellationText={t("confirmationDialog.timeslotChanged.cancel")}
            buttonConfirmationText={t("confirmationDialog.timeslotChanged.confirm")}
            show={showTimeSlotChanged}
            onClose={(shouldReAllocate) => {
              if (shouldReAllocate && booking != null) {
                setShowAllocateVehicle(true);
              }
              setShowTimeSlotChanged(false);
            }}

          />

          <AlertDialog
            title={t("alertDialog.checkInComplete.title")}
            message={t("alertDialog.checkInComplete.message")}
            show={showCheckInComplete}
            onClose={() => {
              setShowCheckInComplete(false);
            }}
            testId="checkin-state-complete"
          />
          <AlertDialog
            title={t("alertDialog.checkOutComplete.title")}
            message={t("alertDialog.checkOutComplete.message")}
            show={showCheckOutComplete}
            onClose={() => {
              setShowCheckOutComplete(false);
            }}
            testId="checkout-complete-dialog"
          />
          <AlertDialog
            title={t("alertDialog.checkoutUndone.title")}
            message={t("alertDialog.checkoutUndone.message")}
            show={showUndoCheckOutComplete}
            onClose={() => {
              setShowUndoCheckOutComplete(false);
            }}
            testId="undo-checkout-complete-dialog"
          />
          <AlertDialog
            title={t("alertDialog.checkInUndone.title")}
            message={t("alertDialog.checkInUndone.message")}
            show={showUndoCheckInComplete}
            onClose={() => {
              setShowUndoCheckInComplete(false);
            }}
            testId="undo-checkin-complete-dialog"
          />

          <ProgressView status={fetchBooking.status}>
            {booking != null && (
              <Fragment>
                <AnimateFrom
                  delay={0.3}
                  style={{
                    opacity: 0,
                    y: -50,
                  }}
                >
                  {/* hidden xsdown */}
                  {/* TODO: DD - look at moving this to logic to css instead */}
                  {showOnSmUp && (
                    <div style={styles.toolBar}>
                      <div style={flex.flexRows}>
                        <div style={flex.flexFill}>
                          <Scaler minScale={0.4}>
                            <h1
                              style={styles.title}
                            >{ellipsis(useFirstName ? `${booking.leadContact.firstName} ${booking.leadContact.lastName}` : `${booking.leadContact.shortName}`, 20)}</h1>
                          </Scaler>
                        </div>
                        <div style={flex.flexAuto}>
                          <div style={styles.overview}>

                            {(expectedReturnDate.length > 0 && system_profile != SystemProfile.fleetManagement) && (
                              <Fragment>
                                <div>
                                  <small>{t("booking.labels.expectedReturn")}</small>
                                </div>
                                <div style={{ fontSize: 20, color: "grey" }}>
                                  <b>{expectedReturnDate}</b>
                                </div>
                                {timeslot}
                              </Fragment>
                            )}

                            <Scaler minScale={0.7}>
                              <h1 style={styles.bookingRef}>
                                {system_profile == SystemProfile.fleetManagement ? (booking.altRef ?? "NO BARCODE") : booking.ref}
                                {releaseAdditionalExternalReferences && useTrackingId && booking.externalRefs && booking.externalRefs.length > 0 && booking.externalRefs[0]?.ref && ` / ${booking.externalRefs[0].ref}`}
                              </h1>
                              <div>
                                {booking.statusId == BookingStatus.Cancelled ? (
                                  <b style={{ fontSize: 20, color: "brown" }}>
                                    {`${deprecateArchiveReleased ? t("booking.labels.cancelled") : t("booking.labels.archived")}`} &nbsp;
                                    {toFormattedDateTimeString(new Date(booking.cancelledDate))}
                                  </b>
                                ) : (
                                  <b
                                    style={{
                                      ...styles.vehicleStatus,
                                      color: palette.vehicleStatus(
                                        booking.vehicle.statusId
                                      ),
                                    }}
                                  >
                                    {translatedStatus}
                                    {renderCollectionDeliveryStatus()}
                                  </b>
                                )}
                              </div>
                              <div>
                                {booking.isWalkIn == true ? (
                                  <strong style={{ color: "blue" }}>{t("sale.alias.walkIn")}</strong>
                                ) : (
                                  <strong style={{ color: "green" }}>
                                    {t("sale.alias.preBooked")} {booking.sourceRef}
                                  </strong>
                                )}
                              </div>
                            </Scaler>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* hidden smup */}
                  {showOnXsDown && (
                    <div style={styles.toolBar}>
                      <div style={flex.flexRows}>
                        <div style={{ ...flex.flexFill, textAlign: "center" }}>
                          {system_profile != SystemProfile.fleetManagement && (
                            <div>
                              {expectedReturnDate.length > 0 && (
                                <Fragment>
                                  <div>
                                    <small>{t("booking.labels.expectedReturn")}</small>
                                  </div>
                                  <div style={{ fontSize: 20, color: "grey" }}>
                                    <b>{expectedReturnDate}</b>
                                  </div>
                                  {timeslot}
                                </Fragment>
                              )}
                            </div>

                          )}

                          <Scaler minScale={0.7}>
                            <h1
                              style={styles.title}
                            >{ellipsis((useFirstName ? `${booking.leadContact.firstName} ${booking.leadContact.lastName}` : `${booking.leadContact.title} ${booking.leadContact.initial} ${booking.leadContact.lastName}`), 15)}</h1>
                            <h1 style={styles.bookingRef}>
                              {booking.ref}
                              {releaseAdditionalExternalReferences && useTrackingId && booking.externalRefs && booking.externalRefs.length > 0 && booking.externalRefs[0]?.ref && ` / ${booking.externalRefs[0].ref}`}
                            </h1>
                            <div>
                              {booking.statusId == BookingStatus.Cancelled ? (
                                <b style={{ fontSize: 20, color: "brown" }}>
                                  {`${deprecateArchiveReleased ? t("booking.labels.cancelled") : t("booking.labels.archived")}`}
                                  {toFormattedDateTimeString(new Date(booking.cancelledDate))}
                                </b>
                              ) : (
                                <b
                                  style={{
                                    ...styles.vehicleStatus,
                                    color: palette.vehicleStatus(
                                      booking.vehicle.statusId
                                    ),
                                  }}
                                >
                                  {translatedStatus}
                                  {renderCollectionDeliveryStatus()}
                                </b>
                              )}
                            </div>
                          </Scaler>
                        </div>
                      </div>
                    </div>
                  )}
                </AnimateFrom>

                <AnimateFrom
                  delay={0.3}
                  style={{
                    opacity: 0,
                    y: 50,
                  }}
                >

                  {headerPanelReleased && !!booking.labels?.length && (
                    <StatusBadges labels={booking.labels} booking={booking} refreshBooking={refreshBooking} />
                  )}
                  {/* TODO: DD - remove the following fragment once the header panel ff is deprecated */}
                  {!headerPanelReleased && (
                    <>
                      {getSalesAndPayments.data != null && (
                        <SaleItemBadges saleItems={getSalesAndPayments.data.saleItems} />
                      )}
                      {!!booking.labels?.length && (
                        <StatusBadges labels={booking.labels} booking={booking} refreshBooking={refreshBooking} />
                      )}
                      {getSalesAndPayments.data != null && (
                        <ProductsCompletedList bookingId={bookingNumericId} saleItems={getSalesAndPayments.data.saleItems} onCompletedListUpdated={() => setProductHistoryTableKey(getNewKey())} />
                      )}
                    </>
                  )}
                  {readOnly == false && (
                    <WorkflowBooking booking={booking} />
                  )}

                  <div style={readOnly == true ? { opacity: 0.6 } : {}}>
                    <Grid container>
                      <BookingFields
                        readOnly={readOnly == true || allowUpdatingBooking == false || !hasPermission("bookingManagement.booking.update")}
                        isNew={false}
                        booking={booking}
                        remarks={listRemarks.data ?? []}
                        remarksSectionRef={remarksSectionRef}
                        saveBooking={(data) => saveBooking(data as Booking)}
                        movement={(data) => movement(data as Booking)}
                        showComplaints={() => {
                          setShowComplaints(true);
                        }}
                        hideNotificationBanners={headerPanelReleased}
                        saleItems={getSalesAndPayments.data?.saleItems ?? undefined}
                        salesAndPayments={getSalesAndPayments.data ?? undefined}
                        bookingId={bookingNumericId}
                        paymentsSectionRef={paymentsSectionRef}
                        onCompletedListUpdated={() => setProductHistoryTableKey(getNewKey())}
                        onRemarkTypeChange={(type) => setRemarkType(type)}
                        headerPanelReleased={headerPanelReleased}
                        isLoadingSalesAndPayments={getSalesAndPayments.status === RequestStatus.loading}
                      />

                      {getSalesAndPayments.data != null && (booking_products_enabled || booking_payments_enabled || booking_packages_enabled) && (
                        <Box ref={paymentsSectionRef} style={{ width: '100%' }}>
                          <BookingProducts
                            showPaymentDialog={showPaymentDialog}
                            openPaymentDialog={() => setShowPaymentDialog(true)}
                            onPaymentDialogClose={(updated: boolean) => {
                              setShowPaymentDialog(false);
                              updated && refreshSalesAndProducts();
                            }}
                            readOnly={readOnly == true || !hasPermission("bookingManagement.booking.update")}
                            booking={booking}
                            booking_products_enabled={booking_products_enabled}
                            booking_payments_enabled={booking_payments_enabled}
                            booking_packages_enabled={booking_packages_enabled}
                            saleData={getSalesAndPayments.data}
                            onReloadRequest={refreshBooking}
                          />
                        </Box>
                      )}

                      <Grid item xs={12}>
                        <NotchedCard
                          style={styles.card}
                          label={t("booking.cards.history")}
                          center={true}
                        >
                          <TabbedView preLoad={false} selectedIndex={0}>
                            <TabbedViewPage title={system_profile == SystemProfile.fleetManagement ? t("booking.labels.status") : t("booking.labels.booking")}>
                              <BookingHistoryTable
                                key={bookingHistoryTableKey}
                                bookingId={booking.id}
                              />
                            </TabbedViewPage>
                            <TabbedViewPage title={t("booking.labels.assets")}>
                              <AssetHistoryTable
                                key={assetHistoryTableKey}
                                bookingId={booking.id}
                              />
                            </TabbedViewPage>


                            <TabbedViewPage title={t("booking.labels.payments")} visible={booking_payments_enabled}>
                              {getSalesAndPayments.data != null && (
                                <PaymentHistoryTable payments={getSalesAndPayments.data.payments} />
                              )}
                            </TabbedViewPage>

                            <TabbedViewPage title={t("booking.labels.products")} visible={booking_products_enabled}>
                              <ProductHistoryTable
                                key={productHistoryTableKey}
                                bookingId={booking.id}
                              ></ProductHistoryTable>
                            </TabbedViewPage>

                            <TabbedViewPage title={t("booking.labels.audits")}>
                              <AuditHistoryTable
                                bookingId={booking.id}
                              ></AuditHistoryTable>
                            </TabbedViewPage>

                            <TabbedViewPage title={t("booking.labels.ingestion")} visible={isSysAdmin(session) && system_profile != SystemProfile.fleetManagement}>
                              <IngestionView bookingId={booking.id}></IngestionView>
                            </TabbedViewPage>


                            <TabbedViewPage title={t("booking.labels.mobile")} visible={isSysAdmin(session)}>
                              <PimTransactionsView bookingId={booking.id}></PimTransactionsView>
                            </TabbedViewPage>
                          </TabbedView>
                        </NotchedCard>
                      </Grid>

                      {/* only show inside grayed container if not nanceled or feature flag is off*/}
                      {!(commentsOnCanceledBookingsReleased && booking.statusId === BookingStatus.Cancelled) && (
                        bookingRemarksV2Released ?
                          // TODO: Make the fallback a dedicated spinner component
                          <Suspense fallback={<ProgressDialog status={RequestStatus.loading} />}>
                            <div ref={remarksSectionRef} style={{ width: '100%' }}>
                              <BookingRemarksV2
                                readOnly={(readOnly == true || (allowUpdatingBooking == false && booking_remarks_always_enabled != true) || !hasPermission("bookingManagement.booking.update"))}
                                isNew={false}
                                booking={booking}
                                remarkType={remarkType}
                                setRemarkType={(value) => setRemarkType(value)}
                                allowCommentsOnCanceled={false}
                              />
                            </div>
                          </Suspense>
                          :
                          <Suspense fallback={<ProgressDialog status={RequestStatus.loading} />}>
                            <div ref={remarksSectionRef} style={{ width: '100%' }}>
                              <BookingRemarks
                                readOnly={(readOnly == true || (allowUpdatingBooking == false && booking_remarks_always_enabled != true) || !hasPermission("bookingManagement.booking.update"))}
                                isNew={false}
                                booking={booking}
                                allowCommentsOnCanceled={false}
                              />
                            </div>
                          </Suspense>
                      )}
                    </Grid>
                  </div>

                </AnimateFrom>

                {/* canceled bookings comments section - it needs to be outside the grayed out container */}
                {commentsOnCanceledBookingsReleased && booking.statusId === BookingStatus.Cancelled && (
                  bookingRemarksV2Released ?
                    // TODO: Make the fallback a dedicated spinner component
                    <Suspense fallback={<ProgressDialog status={RequestStatus.loading} />}>
                      <div ref={remarksSectionRef} style={{ width: '100%' }}>
                        <BookingRemarksV2
                          readOnly={(readOnly == true || (allowUpdatingBooking == false && booking_remarks_always_enabled != true) || !hasPermission("bookingManagement.booking.update"))}
                          isNew={false}
                          booking={booking}
                          remarkType={remarkType}
                          setRemarkType={(value) => setRemarkType(value)}
                          allowCommentsOnCanceled={true}
                        />
                      </div>
                    </Suspense>
                    :
                    <Suspense fallback={<ProgressDialog status={RequestStatus.loading} />}>
                      <div ref={remarksSectionRef} style={{ width: '100%' }}>
                        <BookingRemarks
                          readOnly={(readOnly == true || (allowUpdatingBooking == false && booking_remarks_always_enabled != true) || !hasPermission("bookingManagement.booking.update"))}
                          isNew={false}
                          booking={booking}
                          allowCommentsOnCanceled={true}
                        />
                      </div>
                    </Suspense>
                )}

                {(readOnly == false ||
                  booking.statusId == BookingStatus.Cancelled) && (
                    <AnimateFrom
                      delay={1}
                      style={{
                        opacity: 0,
                        zoom: 5,
                      }}
                    >

                      {isChangeOrderOfFabMenu ? <BookingFabV2
                        controller={bookingFabController}
                        allowUpdatingBooking={allowUpdatingBooking}
                        onRequestLabelPrint={printLabels}
                        booking={booking}
                        onAllocateBooking={() => allocateBooking()}
                        onCheckIn={() => setShowCheckIn(true)}
                        onCheckOut={() => checkOutBooking()}
                        onCancel={() => cancelBooking()}
                        onUnArchive={() => unArchiveBooking()}
                        onShowComplaints={() => setShowComplaints(true)}
                        onShowInspections={() => setShowInspections(true)}
                        onShowDataRetention={() => setShowDataRetention(true)}
                        onShowAllowKeyRelease={() => setShowAllowKeyRelease(true)}
                        onUndoCheckIn={() => setShowUndoCheckIn(true)}
                        onUndoCheckOut={() => undoCheckOut()}
                        onShowFindKey={() => setShowFindKey(true)}
                        onUpdateCollectionDeliveryStatus={handleUpdateCollectionDeliveryStatus}
                        onRestore={() => setShowRestoreBooking(true)}
                      />
                        : <BookingFab
                          controller={bookingFabController}
                          allowUpdatingBooking={allowUpdatingBooking}
                          onRequestLabelPrint={printLabels}
                          booking={booking}
                          onCheckIn={() => setShowCheckIn(true)}
                          onCheckOut={() => checkOutBooking()}
                          onCancel={() => cancelBooking()}
                          onUnArchive={() => unArchiveBooking()}
                          onShowComplaints={() => setShowComplaints(true)}
                          onShowInspections={() => setShowInspections(true)}
                          onShowDataRetention={() => setShowDataRetention(true)}
                          onShowAllowKeyRelease={() => setShowAllowKeyRelease(true)}
                          onUndoCheckIn={() => setShowUndoCheckIn(true)}
                          onUndoCheckOut={() => undoCheckOut()}
                        />
                      }

                    </AnimateFrom>
                  )}
                <Dialog open={showCancelBooking} data-testid="cancel-booking-dialog">
                  <DialogTitle>{deprecateArchiveReleased ? t("general.cancel") : t("general.archive")}</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      {t("booking.cancel.prompt.text")}
                    </DialogContentText>
                    {deprecateArchiveReleased && !isRestoreBookingUncancelReleased && (
                      <DialogContentText variant="caption">
                        {t("booking.cancel.prompt.caption")}
                      </DialogContentText>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={() => {
                        setShowCancelBooking(false);
                      }}
                      data-testid="cancel-booking-no-button"
                    >
                      {deprecateArchiveReleased ? t("general.no") : t("general.cancel")}
                    </Button>
                    <Button
                      onClick={() => {
                        cancelBooking();
                      }}
                      data-testid="cancel-booking-yes-button"
                    >
                      <strong>{deprecateArchiveReleased ? t("general.yes") : t("general.cancel")}</strong>
                    </Button>
                  </DialogActions>
                </Dialog>
                {!deprecateArchiveReleased && (
                  <Dialog open={showUnArchiveBooking}>
                    <DialogTitle>{t("booking.labels.unarchive")}</DialogTitle>
                    <DialogContent>
                      <DialogContentText>
                        {t("confirmationDialog.restoreBooking.message")}
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                      <Button
                        onClick={() => {
                          setShowUnArchiveBooking(false);
                        }}
                      >
                        {t("general.cancel")}
                      </Button>
                      <Button
                        onClick={() => {
                          unArchiveBooking();
                        }}
                      >
                        <b>{t("general.restore")}</b>
                      </Button>
                    </DialogActions>
                  </Dialog>
                )}

                <Dialog open={showRestoreBooking} data-testid="restore-booking-dialog">
                  <DialogTitle>{t("booking.labels.restoreBooking")}</DialogTitle>
                  <DialogContent>
                    <DialogContentText>{t("booking.restore.confirmation")}</DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={() => setShowRestoreBooking(false)}
                      data-testid="restore-booking-cancel-button"
                    >
                      {t("general.cancel")}
                    </Button>
                    <Button
                      onClick={restoreBooking}
                      disabled={isRestoreRequestPending}
                      data-testid="restore-booking-confirm-button"
                    >
                      <strong>{t("general.restore")}</strong>
                    </Button>
                  </DialogActions>
                </Dialog>

                <CheckInDialog show={showCheckIn} booking={booking} onComplete={onCheckInComplete} onCancel={onCheckInCancel} />

                <Dialog open={showAllowKeyRelease}>
                  <DialogTitle>{t("confirmationDialog.customerKeyRelease.title")}</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      <div>{t("confirmationDialog.customerKeyRelease.message")}</div>
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={() => {
                        setShowAllowKeyRelease(false);
                      }}
                    >
                      {t("general.cancel")}
                    </Button>
                    <Button
                      onClick={() => {
                        allowReleaseKey();
                      }}
                    >
                      <b>{t("booking.labels.allowRelease")}</b>
                    </Button>
                  </DialogActions>
                </Dialog>

                <VehicleAllocationPickerDialog
                  show={showAllocateVehicle}
                  booking={booking}
                  onChange={(location) => {
                    if (booking == null || location == null) return;
                    movement({
                      ...{
                        ...booking,
                        vehicle: {
                          ...booking.vehicle,
                          allocatedLane: {
                            ...location,
                            updated: true,
                          },
                        },
                      },
                    });
                    setShowAllocateVehicle(false);
                  }}
                  onCancel={() => {
                    setShowAllocateVehicle(false);
                  }}
                />

                <AlertDialog
                  title={t("alertDialog.keyRelease.title")}
                  message={t("alertDialog.keyRelease.message")}
                  show={showAllowKeyReleaseComplete}
                  onClose={() => {
                    setShowAllowKeyReleaseComplete(false);
                  }}
                />

              </Fragment>
            )}
          </ProgressView>
        </div>
      </div>
      {/* test-id is snackbar-allocation-info */}
      {allocationSnackbarInfo && <SnackAlert
        id={"allocation-info"}
        message={allocationSnackbarInfo}
        autoHideDuration={isAllocating ? null : 3000}
        preventClose={isAllocating}
        onClose={() => { setAllocationSnackbarInfo("") }}
      />}
      {collectionDeliverySnackbarInfo && <SnackAlert
        id={"collection-delivery-info"}
        message={collectionDeliverySnackbarInfo}
        autoHideDuration={isUpdatingCollectionDelivery ? null : 3000}
        preventClose={isUpdatingCollectionDelivery}
        onClose={() => { setCollectionDeliverySnackbarInfo("") }}
      />}
      {restoreSnackbarInfo && (
        <SnackAlert
          id="restore-booking-info"
          message={restoreSnackbarInfo}
          autoHideDuration={isRestoreProgressVisible ? null : 3000}
          preventClose={isRestoreProgressVisible}
          onClose={() => setRestoreSnackbarInfo("")}
        />
      )}
    </DashPage>
  );
};

export default BookingViewPage;
